import React from 'react';
import { InterPoint } from '@saviynt/common';
import {
  Avatar,
  Box,
  Button,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';

import {
  SodAccountChip,
  SodActVsPotChip,
  SodEndPointChip,
  SodRiskPriorityChip,
  SodUserChip,
} from '../../../../../shared/components';
import {
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_SEC_MED,
} from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import { type ListRowAccountState } from '../../../../../shared/types';

import './RemediateScreensShared.css';

/**
 * RemediateScreenLoader
 */
function RemediateScreenLoader() {
  return (
    <Box tag='div' className='RemediationScreen-loaderWrapper'>
      <Loader kind='dots' size='small' format='inline' color='brand' />
    </Box>
  );
}

interface RemediateScreenErrorProps {
  onRefresh: () => void;
  isAccessDenied?: boolean;
}

/**
 * RemediateScreenError
 */
function RemediateScreenError({
  onRefresh,
  isAccessDenied,
}: RemediateScreenErrorProps) {
  const l = useInvertedLocalize();

  return (
    <Box tag='div' className='RemediationScreen-errorWrapper'>
      <Box tag='div' className='RemediationScreen-errorMessage'>
        <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
        <Typography kind='body2' color={CF_CRT_MED}>
          {isAccessDenied
            ? l('You do not have permission to perform this action.')
            : l("We couldn't retrieve the data.")}
        </Typography>
      </Box>
      {!isAccessDenied && (
        <Button kind='filled' size='small' onClick={onRefresh}>
          {l('Refresh')}
        </Button>
      )}
    </Box>
  );
}

RemediateScreenError.defaultProps = {
  isAccessDenied: false,
};

interface RemediateFuntionCenterContentProps {
  order: number;
  fnName: string;
  entCount: number;
}

/**
 * FunctionNameWrapper - Component for wrapping function names
 */
function FunctionNameWrapper({ children }: { children: React.ReactNode }) {
  return (
    <span className='RemediationFunctionScreen-listcenterContent--functionName'>
      {children}
    </span>
  );
}

/**
 * RemediateFuntionCenterContent
 */
function RemediateFuntionCenterContent({
  order,
  fnName,
  entCount,
}: RemediateFuntionCenterContentProps) {
  const l = useInvertedLocalize();

  return (
    <Box
      tag='div'
      className='RemediationFunctionScreen-listcenterContent--isFunction'>
      <Typography kind='h3' color={CF_NEU_BLD} isNoWrap>
        {l(
          'To remediate function {order} ({fnName}) of this violation, revoke the following {entCount} access:',
          {
            order,
            entCount,
            fnName: <FunctionNameWrapper>{fnName}</FunctionNameWrapper>,
          }
        )}
      </Typography>
    </Box>
  );
}

interface RemediateSingletonFuntionCenterContentProps {
  fnNames: string[];
  entCount: number;
}

/**
 * RemediateFuntionCenterContent
 */
function RemediateSingletonFuntionCenterContent({
  fnNames,
  entCount,
}: RemediateSingletonFuntionCenterContentProps) {
  const l = useInvertedLocalize();

  return (
    <Box
      tag='div'
      className='RemediationFunctionScreen-listcenterContent--isFunction'>
      <Typography kind='h3' color={CF_NEU_BLD} isNoWrap lineLimit={1}>
        {l(
          'To remediate function (<functionList></functionList>) of this violation, revoke the following {entCount} access:',
          {
            entCount,
            functionList: () => {
              const parts = new Intl.ListFormat('en', {
                style: 'long',
                type: 'conjunction',
              }).formatToParts(fnNames);

              return parts.map(({ type, value }) =>
                type === 'element' ? (
                  <span
                    key={`${type}-${value}`}
                    className='RemediationFunctionScreen-listcenterContent--functionName'>
                    {value}
                  </span>
                ) : (
                  value
                )
              );
            },
          }
        )}
      </Typography>
    </Box>
  );
}

interface RemediateFunctionAfterContentProps {
  onClick: () => void;
  isValidNextCall: boolean;
}

/**
 * RemediateFunctionAfterContent
 */
function RemediateFunctionAfterContent({
  onClick,
  isValidNextCall,
}: RemediateFunctionAfterContentProps) {
  const l = useInvertedLocalize();

  return (
    <Box tag='div' className='RemediationFunctionScreen-listAfterContent'>
      <Button
        type='button'
        kind='filled'
        size='medium'
        onClick={onClick}
        rightIcon={<Icon kind='ArrowRight' />}>
        {l(isValidNextCall ? 'View All' : 'View Impact')}
      </Button>
    </Box>
  );
}

interface RemediateFunctionAsgEntitlementProps {
  displayName: string;
  entTypeName: string | null;
  entValue: string | null;
}

/**
 * RemediateFunctionAsgEntitlement
 */
function RemediateFunctionAsgEntitlement({
  entTypeName,
  displayName,
  entValue,
}: RemediateFunctionAsgEntitlementProps) {
  const l = useInvertedLocalize();

  return (
    <Box tag='div'>
      <Typography
        kind='body3'
        color={CF_SEC_MED}
        isNoWrap
        className='RemediationFunctionScreen-listcenterContent--isTitle'>
        {l('Assigned Entitlement')}
        <InterPoint />
        {entTypeName}
      </Typography>
      <Box
        tag='div'
        className='RemediationFunctionScreen-listcenterContent--isSubTitle'>
        <Typography kind='h3' color={CF_NEU_BLD} isNoWrap>
          {displayName}
        </Typography>
        <Typography kind='body3' color={CF_SEC_MED}>
          {entValue}
        </Typography>
      </Box>
    </Box>
  );
}

interface RemediateImpactAccountCenterContentProps {
  userName?: string;
  endpoints: {
    endpointName: string | null;
    endpointLogo: string | null;
  }[];
  displayName: string;
}

/**
 * RemediateImpactAccountCenterContent
 */
function RemediateImpactAccountCenterContent({
  userName,
  endpoints,
  displayName,
}: RemediateImpactAccountCenterContentProps) {
  return (
    <Box tag='div' className='RemediationImpactScreen-accountList'>
      <Typography kind='h3' color={CF_NEU_BLD} isNoWrap>
        {userName && userName.trim().length > 0 ? userName : '-'}
      </Typography>
      <Box tag='div' className='RemediationImpactScreen-accountListProps'>
        <SodEndPointChip
          endpoints={endpoints.map((endpoint) => ({
            endpointName: endpoint.endpointName,
            endpointLogo: endpoint.endpointLogo,
          }))}
        />
        <Box tag='div' className='RemediationImpactScreen-accountListUserProp'>
          <Avatar size='xxSmall' />
          <Typography kind='body2' color={CF_NEU_BLD}>
            {displayName}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

RemediateImpactAccountCenterContent.defaultProps = {
  userName: '',
};

interface RemediateImpactViolationCenterContentProps {
  riskName: string;
  riskPriority: string | null;
  riskScore: number | null;
  actualVsPotential: string | null;
  endpoints: {
    endpointName: string | null;
    endpointLogo: string | null;
  }[];
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  accounts: ListRowAccountState[];
}

/**
 * RemediateImpactViolationCenterContent
 */
function RemediateImpactViolationCenterContent({
  riskName,
  riskPriority,
  riskScore,
  actualVsPotential,
  endpoints,
  firstName,
  lastName,
  username,
  accounts,
}: RemediateImpactViolationCenterContentProps) {
  const accountCount = accounts.length;

  return (
    <Box tag='div' className='RemediationImpactScreen-violationList'>
      <Typography kind='h3' color={CF_NEU_BLD} isNoWrap>
        {riskName}
      </Typography>
      <Box tag='div' className='RemediationImpactScreen-accountListProps'>
        <SodRiskPriorityChip priority={riskPriority} riskScore={riskScore} />
        <SodActVsPotChip key='SodActVsPotChip' value={actualVsPotential} />
        {accountCount > 0 && (
          <SodEndPointChip
            key='SodEndPointChip'
            endpoints={endpoints.map((endpoint) => ({
              endpointName: endpoint.endpointName,
              endpointLogo: endpoint.endpointLogo,
            }))}
          />
        )}
        <SodUserChip
          key='SodUserChip'
          firstName={firstName}
          lastName={lastName}
          userName={username}
        />
        {accountCount > 0 && (
          <SodAccountChip
            visibleAccountName={accounts[0].accountName}
            accountCount={accountCount}
            isPrefixInterpoint={false}
          />
        )}
      </Box>
    </Box>
  );
}

export {
  RemediateFunctionAfterContent,
  RemediateFunctionAsgEntitlement,
  RemediateFuntionCenterContent,
  RemediateImpactAccountCenterContent,
  RemediateImpactViolationCenterContent,
  RemediateScreenError,
  RemediateScreenLoader,
  RemediateSingletonFuntionCenterContent,
};
